import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ExternalLink } from 'lucide-react';
import { showPreferencesModal } from '../../../lib/cookie-consent.js';
import { FooterLanguageSelector } from './footer-language-selector.js';

type LinkType = 'internal' | 'external' | 'modal' | 'section' | 'text';

interface FooterLinkEntry {
  key: string;
  type: LinkType;
  target: string;
}

interface FooterCategory {
  titleKey: string;
  links: FooterLinkEntry[];
}

const FOOTER_CATEGORIES: FooterCategory[] = [
  {
    titleKey: 'categories.euLegal',
    links: [
      { key: 'links.privacyPolicy', type: 'internal', target: '/privacy' },
      { key: 'links.cookiePolicy', type: 'internal', target: '/cookie-policy' },
      { key: 'links.termsOfService', type: 'internal', target: '/terms' },
      { key: 'links.impressum', type: 'internal', target: '/impressum' },
      { key: 'links.dpa', type: 'internal', target: '/dpa' },
      { key: 'links.gdprRights', type: 'internal', target: '/gdpr-rights' },
      { key: 'links.subProcessors', type: 'internal', target: '/sub-processors' },
    ],
  },
  {
    titleKey: 'categories.usLegal',
    links: [
      { key: 'links.privacyPolicyCcpa', type: 'internal', target: '/privacy' },
      { key: 'links.doNotSell', type: 'internal', target: '/privacy#ccpa' },
      { key: 'links.termsOfService', type: 'internal', target: '/terms' },
    ],
  },
  {
    titleKey: 'categories.paymentBilling',
    links: [
      { key: 'links.refundPolicy', type: 'internal', target: '/refund-policy' },
      { key: 'links.billingTerms', type: 'internal', target: '/billing-terms' },
      { key: 'links.paymentsByStripe', type: 'text', target: '' },
      { key: 'links.pricing', type: 'section', target: '/#pricing' },
    ],
  },
  {
    titleKey: 'categories.accessibility',
    links: [
      { key: 'links.accessibilityStatement', type: 'internal', target: '/accessibility-statement' },
      { key: 'links.feedbackMechanism', type: 'internal', target: '/contact' },
    ],
  },
  {
    titleKey: 'categories.trustCompliance',
    links: [
      { key: 'links.securityPolicy', type: 'internal', target: '/security' },
      { key: 'links.acceptableUse', type: 'internal', target: '/acceptable-use' },
      { key: 'links.dmcaPolicy', type: 'internal', target: '/dmca' },
    ],
  },
  {
    titleKey: 'categories.standardSaas',
    links: [
      { key: 'links.contact', type: 'internal', target: '/contact' },
      { key: 'links.docs', type: 'internal', target: '/docs' },
      { key: 'links.faq', type: 'internal', target: '/faq' },
      { key: 'links.blog', type: 'internal', target: '/blog' },
      { key: 'links.changelog', type: 'internal', target: '/changelog' },
    ],
  },
];

const LINK_CLASS =
  'link-underline-draw relative text-caption text-secondary hover:text-accent-blue transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400';

function FooterLinkItem({ link, label }: { link: FooterLinkEntry; label: string }) {
  switch (link.type) {
    case 'internal':
    case 'section':
      return (
        <Link to={link.target} className={LINK_CLASS}>
          {label}
        </Link>
      );
    case 'external':
      return (
        <a
          href={link.target}
          target="_blank"
          rel="noopener noreferrer"
          className={`${LINK_CLASS} inline-flex items-center`}
          aria-label={`${label} (opens in new tab)`}
        >
          {label}
          <ExternalLink className="ml-1 h-3 w-3 shrink-0" aria-hidden="true" />
        </a>
      );
    case 'modal':
      return (
        <button
          type="button"
          onClick={() => showPreferencesModal()}
          className={`${LINK_CLASS} text-left cursor-pointer`}
        >
          {label}
        </button>
      );
    case 'text':
      return <span className="text-caption text-muted">{label}</span>;
    default: {
      const _exhaustive: never = link.type;
      return _exhaustive;
    }
  }
}

export function Footer() {
  const { t } = useTranslation('legal');

  return (
    <footer className="border-t border-[rgba(255,255,255,0.04)] bg-canvas px-6 py-10">
      <div className="mx-auto max-w-[1200px]">
        <div className="grid grid-cols-2 gap-8 compact:grid-cols-3 standard:grid-cols-6">
          {FOOTER_CATEGORIES.map((category) => (
            <div key={category.titleKey}>
              <h3 className="text-small font-medium text-primary mb-3">{t(category.titleKey)}</h3>
              <ul className="flex flex-col gap-1.5">
                {category.links.map((link) => (
                  <li key={link.key}>
                    <FooterLinkItem link={link} label={t(link.key)} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-8 pt-6 border-t border-[rgba(255,255,255,0.04)] flex items-center justify-between text-caption text-secondary">
          <span>{t('copyright')}</span>
          <FooterLanguageSelector />
        </div>
      </div>
    </footer>
  );
}
